import { useCallback, useState, useEffect } from 'react'
import constants from '../../constants'

const MIN_AGE = constants.MIN_AGE
const MAX_AGE = constants.MAX_AGE

export const getYearArray = (minAge = MIN_AGE, maxAge = MAX_AGE) => {
  const currentYear = new Date().getFullYear()
  const minYear = currentYear - maxAge
  const maxYear = currentYear - minAge
  return Array.from(
    { length: maxAge - minAge + 1 },
    (_, i) => `${minYear + i}`,
  ).filter((year) => parseInt(year) <= maxYear)
}

export const MONTHS = [
  { name: 'January', value: '01' },
  { name: 'February', value: '02' },
  { name: 'March', value: '03' },
  { name: 'April', value: '04' },
  { name: 'May', value: '05' },
  { name: 'June', value: '06' },
  { name: 'July', value: '07' },
  { name: 'August', value: '08' },
  { name: 'September', value: '09' },
  { name: 'October', value: '10' },
  { name: 'November', value: '11' },
  { name: 'December', value: '12' },
]

export const getMonthArray = (
  selectedYear,
  minAge = MIN_AGE,
  maxAge = MAX_AGE,
) => {
  const currentDate = new Date()
  const currentYear = currentDate.getFullYear()
  const currentMonth = currentDate.getMonth() + 1
  const minYear = currentYear - maxAge
  const maxYear = currentYear - minAge

  return MONTHS.filter((month) => {
    const monthNum = parseInt(month.value)
    if (parseInt(selectedYear) === maxYear) {
      return monthNum <= currentMonth
    } else if (parseInt(selectedYear) === minYear) {
      return monthNum >= currentMonth
    }
    return true
  })
}

export const getDayArray = (
  year,
  month,
  minAge = MIN_AGE,
  maxAge = MAX_AGE,
) => {
  const dayCount = new Date(year, month, 0).getDate()
  const days = Array.from({ length: dayCount }, (_, i) =>
    String(i + 1).padStart(2, '0'),
  )

  const currentDate = new Date()
  const currentYear = currentDate.getFullYear()
  const currentMonth = currentDate.getMonth() + 1
  const currentDay = currentDate.getDate()
  const minYear = currentYear - maxAge
  const maxYear = currentYear - minAge

  if (parseInt(year) === maxYear && month === currentMonth) {
    return days.filter((day) => parseInt(day) <= currentDay)
  } else if (parseInt(year) === minYear && month === currentMonth) {
    return days.filter((day) => parseInt(day) >= currentDay)
  }

  return days
}

const useDatePicker = ({
  value,
  onChange,
  name,
  minAge = MIN_AGE,
  maxAge = MAX_AGE,
}) => {
  const currentDate = new Date()
  const defaultYear = (currentDate.getFullYear() - minAge).toString()

  // Function to check if a date is within the allowed age range
  const isDateInAgeRange = (year, month, day) => {
    const selectedDate = new Date(
      parseInt(year),
      parseInt(month) - 1,
      parseInt(day),
    )
    const minDate = new Date(currentDate)
    minDate.setFullYear(currentDate.getFullYear() - maxAge)
    const maxDate = new Date(currentDate)
    maxDate.setFullYear(currentDate.getFullYear() - minAge)

    return selectedDate >= minDate && selectedDate <= maxDate
  }

  // Initialize state with value from props if available
  const getInitialState = () => {
    if (value) {
      try {
        const [year, month, day] = value.split('-').map((val) => val.trim())
        if (year && month && day) {
          // Check if the date is within the allowed age range
          if (isDateInAgeRange(year, month, day)) {
            const monthObj = MONTHS[parseInt(month) - 1]
            return {
              year: year.toString(),
              month: monthObj?.name || MONTHS[currentDate.getMonth()].name,
              day: day.padStart(2, '0'),
            }
          }
        }
      } catch (error) {
        console.error('Error parsing date value:', error)
      }
    }

    // If value is invalid or outside range, use default value
    return {
      year: defaultYear,
      month: MONTHS[currentDate.getMonth()].name,
      day: currentDate.getDate().toString().padStart(2, '0'),
    }
  }

  const [pickerValue, setPickerValue] = useState(getInitialState)

  // Update state when value prop changes
  useEffect(() => {
    if (value) {
      try {
        const [year, month, day] = value.split('-').map((val) => val.trim())
        if (year && month && day) {
          // Check if the date is within the allowed age range
          if (isDateInAgeRange(year, month, day)) {
            const monthObj = MONTHS[parseInt(month) - 1]
            setPickerValue({
              year: year.toString(),
              month: monthObj?.name || MONTHS[currentDate.getMonth()].name,
              day: day.padStart(2, '0'),
            })
          } else {
            // If date is outside range, reset to default value
            setPickerValue({
              year: defaultYear,
              month: MONTHS[currentDate.getMonth()].name,
              day: currentDate.getDate().toString().padStart(2, '0'),
            })
          }
        }
      } catch (error) {
        console.error('Error updating date value:', error)
      }
    }
  }, [value, defaultYear])

  const availableYears = getYearArray(minAge, maxAge)
  const availableMonths = getMonthArray(pickerValue.year, minAge, maxAge)
  const availableDays = getDayArray(
    Number(pickerValue.year),
    parseInt(MONTHS.find((m) => m.name === pickerValue.month).value),
    minAge,
    maxAge,
  )

  // Ensure selected year is within available years
  useEffect(() => {
    if (pickerValue.year && !availableYears.includes(pickerValue.year)) {
      const newYear = availableYears[0]
      setPickerValue((prev) => ({
        ...prev,
        year: newYear,
      }))
    }
  }, [pickerValue.year, availableYears])

  const handleNavigation = (type, direction) => {
    const currentIndex = {
      day: availableDays.indexOf(pickerValue.day),
      month: availableMonths.findIndex((m) => m.name === pickerValue.month),
      year: availableYears.indexOf(pickerValue.year),
    }[type]

    const availableItems = {
      day: availableDays,
      month: availableMonths.map((m) => m.name),
      year: availableYears,
    }[type]

    const newIndex = currentIndex + (direction === 'next' ? 1 : -1)
    if (newIndex >= 0 && newIndex < availableItems.length) {
      const newValue = {
        ...pickerValue,
        [type]: availableItems[newIndex],
      }
      handlePickerChange(newValue, type)
    }
  }

  const handlePickerChange = useCallback(
    (newValue, key) => {
      const selectedDate = new Date(
        parseInt(newValue.year),
        MONTHS.findIndex((m) => m.name === newValue.month),
        parseInt(newValue.day),
      )

      const currentDate = new Date()
      const minDate = new Date(currentDate)
      minDate.setFullYear(currentDate.getFullYear() - maxAge)
      const maxDate = new Date(currentDate)
      maxDate.setFullYear(currentDate.getFullYear() - minAge)

      // Prevent selecting dates outside age range
      if (selectedDate < minDate || selectedDate > maxDate) {
        return
      }

      setPickerValue(newValue)
      const monthNumber = MONTHS.find((m) => m.name === newValue.month).value
      const formattedDate = `${newValue.year}-${monthNumber}-${newValue.day}`
      if (onChange) {
        onChange(name, formattedDate)
      }

      // If month or year changes, adjust day if needed
      if (key === 'month' || key === 'year' || key === 'day') {
        const { year, month } = newValue
        const monthNumber = parseInt(MONTHS.find((m) => m.name === month).value)
        const newDayArray = getDayArray(
          Number(year),
          monthNumber,
          minAge,
          maxAge,
        )
        if (!newDayArray.includes(newValue.day)) {
          const newDay = newDayArray[newDayArray.length - 1]
          const updatedValue = { ...newValue, day: newDay }
          setPickerValue(updatedValue)
          const updatedMonthNumber = MONTHS.find(
            (m) => m.name === updatedValue.month,
          ).value
          const updatedFormattedDate = `${updatedValue.year}-${updatedMonthNumber}-${updatedValue.day}`
          if (onChange) {
            onChange(name, updatedFormattedDate)
          }
        }
      }
    },
    [minAge, maxAge, name, onChange],
  )

  return {
    pickerValue,
    availableMonths,
    availableDays,
    availableYears,
    setPickerValue,
    handleNavigation,
    handlePickerChange,
  }
}

export default useDatePicker
